import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PteCoreWritingTest from './PteCoreWritingTest.tsx';
import Ptecorereadingtest from './ptecorereadingtest.js';
import PteCoreSpeakingTest from './PteCoreSpeakingTest.tsx';
import PteCoreListeningTest from './PteCoreListeningTest.tsx';
import { BookOpen, Headphones, Mic } from 'lucide-react';

// Section types
type TestSection = 'intro' | 'speaking' | 'writing' | 'reading' | 'listening' | 'completed';

const PteCoreFullTest: React.FC = () => {
    const { testNumber } = useParams<{ testNumber: string }>();
    const navigate = useNavigate();
    const [currentSection, setCurrentSection] = useState<TestSection>('intro');

    // Scroll to top when section changes
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentSection]);

    const handleStartTest = () => {
        // PTE Core order: Speaking -> Writing -> Reading -> Listening
        setCurrentSection('speaking');
    };

    const handleSpeakingComplete = () => {
        // Move to Writing (Part 1 continued)
        setCurrentSection('writing');
    };

    const handleWritingComplete = () => {
        // Move to Reading (Part 2)
        setCurrentSection('reading');
    };

    const handleReadingComplete = () => {
        // Move to Listening (Part 3)
        // Since we don't have Listening yet, we'll go to completed
        setCurrentSection('listening');
    };

    const handleListeningComplete = () => {
        setCurrentSection('completed');
    };

    const renderIntro = () => (
        <div className="max-w-4xl mx-auto mt-10 p-8 bg-white rounded-xl shadow-lg">
            <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">PTE Core Full Mock Test {testNumber}</h1>

            <div className="space-y-6 text-gray-600 mb-8">
                <p className="text-lg">
                    This full mock test simulates the actual PTE Core exam experience.
                    The test is divided into three parts:
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="border p-4 rounded-lg bg-blue-50 border-blue-100">
                        <div className="flex items-center gap-2 mb-2 text-blue-800 font-bold">
                            <Mic size={20} />
                            <span>Part 1: Speaking & Writing</span>
                        </div>
                        <p className="text-sm">50-60 minutes</p>
                    </div>

                    <div className="border p-4 rounded-lg bg-green-50 border-green-100">
                        <div className="flex items-center gap-2 mb-2 text-green-800 font-bold">
                            <BookOpen size={20} />
                            <span>Part 2: Reading</span>
                        </div>
                        <p className="text-sm">30 minutes</p>
                    </div>

                    <div className="border p-4 rounded-lg bg-purple-50 border-purple-100">
                        <div className="flex items-center gap-2 mb-2 text-purple-800 font-bold">
                            <Headphones size={20} />
                            <span>Part 3: Listening</span>
                        </div>
                        <p className="text-sm">30 minutes</p>
                    </div>
                </div>

                <div className="bg-green-50 border-l-4 border-green-400 p-4">
                    <p className="text-sm text-green-800">
                        <strong>Ready:</strong> All sections (Speaking, Writing, Reading, Listening) are fully implemented.
                    </p>
                </div>
            </div>

            <div className="flex justify-center">
                <button
                    onClick={handleStartTest}
                    className="px-8 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors shadow-md text-lg"
                >
                    Start Full Test
                </button>
            </div>
        </div>
    );



    const renderCompleted = () => (
        <div className="max-w-4xl mx-auto mt-10 p-8 bg-white rounded-xl shadow-lg text-center">
            <div className="flex justify-center mb-6">
                <div className="p-4 bg-green-100 rounded-full text-green-600">
                    <BookOpen size={48} />
                </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Test Completed!</h2>
            <p className="text-gray-600 mb-8">
                You have completed the available sections of the PTE Core Full Mock Test.
                Please check your dashboard for detailed results.
            </p>
            <button
                onClick={() => navigate('/pte/dashboardpte')}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors"
            >
                Return to Dashboard
            </button>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* Progress Bar */}


            {currentSection === 'intro' && renderIntro()}

            {/* We pass a special prop 'isFullTest' or 'onComplete' to components if we modify them.
                For now, since we haven't modified them yet, they will behave as standalone.
                We need to modify them to accept an onComplete callback.
            */}

            {currentSection === 'speaking' && (
                <div className="pt-4">
                    <PteCoreSpeakingTest
                        isFullTest={true}
                        onSectionComplete={handleSpeakingComplete}
                    />
                </div>
            )}

            {currentSection === 'writing' && (
                <div className="pt-4">
                    <PteCoreWritingTest
                        isFullTest={true}
                        onSectionComplete={handleWritingComplete}
                    />
                </div>
            )}

            {currentSection === 'reading' && (
                <div className="pt-4">
                    <Ptecorereadingtest
                        isFullTest={true}
                        onSectionComplete={handleReadingComplete}
                    />
                </div>
            )}

            {currentSection === 'listening' && (
                <div className="pt-4">
                    <PteCoreListeningTest
                        isFullTest={true}
                        onSectionComplete={handleListeningComplete}
                    />
                </div>
            )}

            {currentSection === 'completed' && renderCompleted()}
        </div>
    );
};

export default PteCoreFullTest;
